import { z } from 'zod';
import { centavos, pageQuery } from './envelope.ts';
import { transactionStatus } from './lists.ts';
import type { PlayerLedgerKind } from './player-money.ts';
import { AUDIT_REASON_MIN_LENGTH } from './audit-reasons.ts';

/**
 * §7 Agent management — the request shapes for every write in the module.
 *
 * Its own file rather than more of `lists.ts`, for the same reason
 * `affiliate.ts` is: the agent domain is structurally independent — a
 * hierarchy, an override differential, a downline that is resolved as of a
 * date — and folding its contracts into the shared list file is how two
 * domains start borrowing each other's assumptions.
 *
 * Every reason field below is REQUIRED and every code is a CLOSED SET. A
 * capability that changes what a partner is paid, or whether they may trade at
 * all, leaves a row a regulator reads; "why" is not an optional field on that
 * row, and a report that counts decisions by cause cannot group on a sentence
 * somebody typed.
 */

export const agentTier = z.enum(['MASTER', 'SUB_AGENT']);
export type AgentTier = z.infer<typeof agentTier>;

/**
 * A reason an operator has to write, floored where the database floors it.
 *
 * It said eight characters until item 41 landed, and the database says ten. The
 * handler writes the operator's own sentence into the audit row unchanged — which
 * is the right thing to do, since the sentence IS the reason — so a nine-character
 * reason passed this check, reached `platform.audit_log`, and came back as
 * **409 "That change is not allowed by the platform rules."** An operator one
 * character short was told the platform forbids what they were doing.
 *
 * `AUDIT_REASON_MIN_LENGTH` is the database's own floor, so the two cannot drift
 * again, and the message carries the number rather than restating it.
 */
const reason = z
  .string()
  .trim()
  .min(
    AUDIT_REASON_MIN_LENGTH,
    `Give a reason of at least ${AUDIT_REASON_MIN_LENGTH} characters — it is the audit trail.`,
  )
  .max(500);

const basisPoints = z
  .number()
  .int('A rate is an integer number of basis points. 5.0% is 500.')
  .min(0)
  .max(10_000, 'A rate cannot exceed 100%.');

/* ── Creating an agent ───────────────────────────────────────────────────── */

/**
 * A new agent.
 *
 * `parentId` is a MASTER's id and is required for a sub-agent, illegal for a
 * master — the same rule `agent_tier_parent_coherent` enforces in the database.
 * The refinement restates it here so the operator is told which field is wrong
 * instead of reading a constraint name.
 *
 * The agent lands in PENDING_KYB. That is not a field the caller may set: an
 * agent that could be created ACTIVE would be an agent that never passed KYB.
 */
export const createAgentRequest = z
  .object({
    name: z.string().trim().min(2).max(200),
    brandId: z.string().min(1),
    tier: agentTier,
    parentId: z.string().min(1).nullish(),
    area: z.string().trim().max(80).optional(),
    /**
     * The plan the agent starts on. Optional: a network sometimes onboards an
     * agent on a hand-agreed rate before a plan exists for it.
     */
    planId: z.string().min(1).nullish(),
    /**
     * Required only when no plan is given. With a plan, the rate comes from the
     * plan's entry band — two sources for one number is how they drift apart.
     */
    commissionRateBp: basisPoints.optional(),
  })
  .refine((v) => v.tier !== 'SUB_AGENT' || Boolean(v.parentId), {
    path: ['parentId'],
    message: 'A sub-agent needs a master to sit under.',
  })
  .refine((v) => v.tier !== 'MASTER' || !v.parentId, {
    path: ['parentId'],
    message: 'A master sits at the top of the tree and cannot have a parent.',
  })
  .refine((v) => Boolean(v.planId) || v.commissionRateBp !== undefined, {
    path: ['commissionRateBp'],
    message: 'Choose a commission plan, or set a rate for this agent.',
  });
export type CreateAgentRequest = z.infer<typeof createAgentRequest>;

/* ── The KYB verdict ─────────────────────────────────────────────────────── */

/**
 * The outcome an operator RECORDS by hand.
 *
 * There is no identity-verification provider wired into this platform. Nothing
 * runs KYB; a person reads whatever the business supplied and writes down what
 * they found. PASSED activates the agent, FAILED suspends it with the reason
 * attached — there is no third state, because "we are still looking" is what
 * PENDING_KYB already means.
 */
export const kybOutcome = z.enum(['PASSED', 'FAILED']);
export type KybOutcome = z.infer<typeof kybOutcome>;

export const recordKybRequest = z.object({
  outcome: kybOutcome,
  reason,
});
export type RecordKybRequest = z.infer<typeof recordKybRequest>;

/* ── Suspension and reinstatement ────────────────────────────────────────── */

export const suspendAgentRequest = z.object({ reason });
export type SuspendAgentRequest = z.infer<typeof suspendAgentRequest>;

export const reinstateAgentRequest = z.object({ reason });
export type ReinstateAgentRequest = z.infer<typeof reinstateAgentRequest>;

/* ── Commission plans ────────────────────────────────────────────────────── */

export const planTier = z.enum(['MASTER', 'SUB_AGENT', 'ANY']);
export const planKind = z.enum(['FLAT', 'TIERED', 'RETAINER']);
export const planSettlement = z.enum(['WEEKLY', 'FORTNIGHTLY', 'MONTHLY']);

/**
 * One band of a plan: a rate that applies from `minNetRevenue` upwards.
 *
 * Centavos as a string, like every other amount on the wire — ₱10,000,000.00 is
 * "1000000000" and a JSON number would already be rounding by the time a real
 * threshold got there.
 */
export const commissionBandInput = z.object({
  minNetRevenue: centavos,
  rateBp: basisPoints,
});
export type CommissionBandInput = z.infer<typeof commissionBandInput>;

/**
 * A new plan.
 *
 * The refinements below are the same rules `agent.assert_plan_shape` enforces
 * at COMMIT, restated where the operator can still fix the form. The database
 * is what actually holds; this is what makes the refusal readable.
 */
export const createCommissionPlanRequest = z
  .object({
    name: z.string().trim().min(3).max(120),
    brandId: z.string().min(1),
    tier: planTier,
    kind: planKind,
    settlement: planSettlement,
    /** Centavos per settlement period. "0" on a plan with no retainer. */
    retainerAmount: centavos.default('0'),
    /** Months a negative period is carried before it is written off. 0 = forever. */
    carryOverMonths: z.number().int().min(0).max(60).default(0),
    bands: z.array(commissionBandInput).min(1).max(8),
    note: z.string().trim().max(500).optional(),
  })
  .refine((v) => v.bands.some((b) => b.minNetRevenue === '0'), {
    path: ['bands'],
    message: 'The lowest band must start at ₱0 — revenue below it would have no rate.',
  })
  .refine((v) => v.kind !== 'FLAT' || v.bands.length === 1, {
    path: ['bands'],
    message: 'A flat plan has exactly one band. A plan with a threshold is tiered.',
  })
  .refine((v) => v.kind !== 'TIERED' || v.bands.length >= 2, {
    path: ['bands'],
    message: 'A tiered plan needs at least two bands — a tier needs a threshold to cross.',
  })
  .refine((v) => v.kind !== 'RETAINER' || BigInt(v.retainerAmount) > 0n, {
    path: ['retainerAmount'],
    message: 'A retainer plan needs a retainer amount.',
  })
  .refine((v) => v.kind === 'RETAINER' || BigInt(v.retainerAmount) === 0n, {
    path: ['retainerAmount'],
    message: 'Only a retainer plan carries a retainer. Change the kind to Retainer.',
  })
  .refine(
    (v) => {
      const sorted = [...v.bands].toSorted((a, b) =>
        BigInt(a.minNetRevenue) < BigInt(b.minNetRevenue) ? -1 : 1,
      );
      return sorted.every((b, i) => i === 0 || b.rateBp > sorted[i - 1]!.rateBp);
    },
    {
      path: ['bands'],
      message:
        'Each band must pay more than the one beneath it. Bands are whole-amount, so a rate that does not rise would pay more for earning less.',
    },
  );
export type CreateCommissionPlanRequest = z.infer<typeof createCommissionPlanRequest>;

export const archiveCommissionPlanRequest = z.object({ reason });
export type ArchiveCommissionPlanRequest = z.infer<typeof archiveCommissionPlanRequest>;

/**
 * Put an agent on a plan.
 *
 * There is deliberately no `effectiveFrom` here. The date is the start of the
 * next settlement period, computed by the server from
 * `agent.commission_period`; letting a caller name it would let a caller name
 * a date inside a settled period, and "never retroactively" would be a
 * sentence on a screen again.
 */
export const assignCommissionPlanRequest = z.object({
  planId: z.string().min(1),
  reason,
});
export type AssignCommissionPlanRequest = z.infer<typeof assignCommissionPlanRequest>;

/* ── §7 The agent portal's two money screens ─────────────────────────────────
   Appended for `GET /api/agent/transactions` and `GET /api/agent/withdrawals`,
   which had no route and no query shape. Both are READS and both are scoped by
   the agent id on the session cookie, so there is deliberately no agent id, no
   player id and no brand id in either schema below: a field a caller could set
   is a field a caller could point at somebody else's players.

   Every value here reaches SQL as a PARAMETER. The closed sets exist anyway,
   for the reason `PLAYER_LEDGER_KINDS` states: a caller-chosen value compared
   against a column is only safe when the set it can take is fixed in code, and
   the `sort` values do not reach SQL as values at all — they select a fixed
   fragment from a closed map in `services/api/src/list-query.ts`. */

/**
 * What the Transactions screen lists: the player's own money movements.
 *
 * A SUBSET of `PLAYER_LEDGER_KINDS`, and typed as one so a kind that is not
 * player-facing cannot be added here by accident. Two kinds are left out of the
 * subset on purpose, and the screen says both out loud rather than letting the
 * omission read as "your players did none of this":
 *
 *   BET and WIN are round-level movements. They are player-facing, but the
 *   screen is headed "Deposits, payouts and bonuses" and one player's evening
 *   is a hundred of them — the seed writes 40–140 rounds per player against a
 *   handful of deposits. Listing them would bury the rows the screen exists
 *   for, and the agent already has that money as GGR on Earnings.
 *
 * COMMISSION and BONUS_CONVERT are absent because `PLAYER_LEDGER_KINDS` does
 * not contain them: the first is the operator's ledger with its partners and
 * belongs to no player, and the second moves PLAYER_BONUS to PLAYER_CASH — both
 * sides on the same player — so it nets to zero against the player's own
 * accounts and would render as ₱0.00 beside a real reference.
 */
export const AGENT_TRANSACTION_KINDS = [
  'DEPOSIT',
  'WITHDRAWAL',
  'BONUS_GRANT',
  'BONUS_FORFEIT',
  'ADJUSTMENT',
  'REVERSAL',
] as const satisfies readonly PlayerLedgerKind[];

export type AgentTransactionKind = (typeof AGENT_TRANSACTION_KINDS)[number];

/**
 * The orders the ledger may be read in.
 *
 * Three, because three are BACKED. There is no "largest first": the amount on a
 * row is summed from `wallet.ledger_entry` per transaction rather than stored,
 * so ordering by it would order by a correlated subquery over the whole
 * attributed set — and the screen renders that control disabled with the reason
 * in visible text instead of offering a sort that would time out.
 */
export const agentTransactionSort = z.enum(['NEWEST', 'OLDEST', 'PLAYER']);
export type AgentTransactionSort = z.infer<typeof agentTransactionSort>;

export const agentTransactionQuery = pageQuery.extend({
  /** Absent means every kind in `AGENT_TRANSACTION_KINDS` — the "All" chip. */
  kind: z.enum(AGENT_TRANSACTION_KINDS).optional(),
  /** SETTLED · PENDING · AWAITING_CHECKER · FAILED · REVERSED. */
  status: transactionStatus.optional(),
  /** Matched against the transaction ref and the player's name and ref. */
  q: z.string().trim().min(1).max(120).optional(),
  sort: agentTransactionSort.default('NEWEST'),
});
export type AgentTransactionQuery = z.infer<typeof agentTransactionQuery>;

/**
 * The stages a withdrawal passes through, as `payment.withdrawal.status` holds
 * them. Restated as a contract rather than imported from the back office's
 * queue filter, because the two answer different questions: the console filters
 * the WORK QUEUE and cares only about what is pending, and a partner is reading
 * the history of their own players' payouts, where REJECTED and FAILED are the
 * outcomes they most need to see.
 */
export const AGENT_WITHDRAWAL_STAGES = [
  'AWAITING_CHECKER',
  'HELD_BY_RISK',
  'RELEASED',
  'REJECTED',
  'FAILED',
] as const;
export const agentWithdrawalStage = z.enum(AGENT_WITHDRAWAL_STAGES);
export type AgentWithdrawalStage = z.infer<typeof agentWithdrawalStage>;

/**
 * OLDEST is the default, and it is not an arbitrary one: the design states the
 * payout queue is "ordered oldest-first; SLA is 2 hours from request to
 * release", so the row closest to breaching is the row at the top.
 */
export const agentWithdrawalSort = z.enum(['OLDEST', 'NEWEST', 'LARGEST']);
export type AgentWithdrawalSort = z.infer<typeof agentWithdrawalSort>;

export const agentWithdrawalQuery = pageQuery.extend({
  status: agentWithdrawalStage.optional(),
  /** Matched against the request ref and the player's name and ref. */
  q: z.string().trim().min(1).max(120).optional(),
  sort: agentWithdrawalSort.default('OLDEST'),
});
export type AgentWithdrawalQuery = z.infer<typeof agentWithdrawalQuery>;
